/**
 * Pure HTML builders for the Magic Sign email preview tab. No React/hook dependency —
 * moved out of ComposeEmailModal.tsx (which only renders JSX + wires callbacks) so this
 * business logic lives alongside composeEmailUtils.ts / dataPendingTable.ts.
 * Pattern reference: composeEmailUtils.ts (pure helpers colocated with the modal).
 */
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import type { NonEditableLink } from './types';

// Preview-only styling constants - used solely to render the signing-links "Review and Sign"
// buttons and the logo strip inside the generated preview HTML document.
const PREVIEW_ACCENT_COLOR = '#6021B3';
const PREVIEW_BUTTON_TEXT_COLOR = '#ffffff';
const PREVIEW_META_TEXT_COLOR = '#555';
const PREVIEW_LOGO_MAX_HEIGHT_PX = 40;
const PREVIEW_LOGO_MAX_WIDTH_PX = 180;

/** Minimal text escaper for values interpolated into the preview HTML document. */
export const escapeText = (value: string): string =>
    value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * Builds the full HTML for the Magic Sign email preview that opens in a new tab.
 * Renders the exact details the recipient will receive - To, Subject, the sender's
 * note, and a per-signatory "Review and Sign" button linking to their signing link -
 * mirroring the general Data Pending preview pattern (preview opens in its own tab).
 */
export const buildMagicSignPreviewHtml = (args: {
    recipients: Array<{ emailId: string; name?: string }>;
    subject: string;
    noteHtml: string;
    links: NonEditableLink[];
    logoUrl?: string;
}): string => {
    const { recipients, subject, noteHtml, links, logoUrl } = args;
    // PNO-branded logo - shown INSIDE the email body (top of the content), matching how the
    // standard AICA emails (Data Pending / Compose Email) render the logo, not as a header strip
    // above the To/Subject meta.
    const logoInBody = logoUrl
        ? `<div style="margin-bottom:20px;"><img src="${encodeURI(
              logoUrl,
          )}" alt="" style="max-height:${PREVIEW_LOGO_MAX_HEIGHT_PX}px;max-width:${PREVIEW_LOGO_MAX_WIDTH_PX}px;" /></div>`
        : '';
    const recipientsText = recipients.map((r) => (r.name ? `${r.name} <${r.emailId}>` : r.emailId)).join(', ');
    const safeNote = noteHtml && noteHtml !== '<div></div>' ? sanitizeHtml(noteHtml) : '';
    const buttons = links
        .map(
            (l) => `
            <div style="margin:16px 0;">
                <div style="font-size:13px;color:${PREVIEW_META_TEXT_COLOR};margin-bottom:8px;">${escapeText(l.name)}${
                    l.orgName ? ' &mdash; ' + escapeText(l.orgName) : ''
                }</div>
                <a href="${encodeURI(l.link)}" target="_blank" rel="noopener noreferrer"
                   style="display:inline-block;background:${PREVIEW_ACCENT_COLOR};color:${PREVIEW_BUTTON_TEXT_COLOR};text-decoration:none;padding:12px 24px;border-radius:6px;font-family:Arial,sans-serif;font-size:14px;font-weight:600;">Review and Sign</a>
            </div>`,
        )
        .join('');
    return `<!doctype html><html lang="en"><head><meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Email Preview</title></head>
        <body style="margin:0;background:#f4f4f7;font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;">
            <div style="max-width:600px;margin:24px auto;background:#ffffff;border-radius:8px;overflow:hidden;border:1px solid #ececec;">
                <div style="padding:16px 24px;border-bottom:1px solid #ececec;font-size:12px;color:#777;line-height:1.8;">
                    <div><strong>To:</strong> ${escapeText(recipientsText) || '&mdash;'}</div>
                    <div><strong>Subject:</strong> ${escapeText(subject)}</div>
                </div>
                <div style="padding:24px;font-size:14px;line-height:1.6;">
                    ${logoInBody}
                    ${safeNote}
                    ${buttons}
                </div>
            </div>
        </body></html>`;
};

// Grace period before revoking the preview blob URL — window.open() returns before the new
// tab has actually finished loading the blob, so revoking synchronously right after risks the
// tab showing a broken/empty page in some browsers. A short delay lets the navigation complete
// while still freeing the blob (previously never revoked at all).
const PREVIEW_TAB_REVOKE_DELAY_MS = 2000;

/**
 * Opens the Magic Sign email preview in a new browser tab, then revokes the blob URL after
 * PREVIEW_TAB_REVOKE_DELAY_MS so every Preview click no longer leaks a blob for the session.
 */
export const openMagicSignPreviewTab = (html: string): void => {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), PREVIEW_TAB_REVOKE_DELAY_MS);
};
